'use client'

import { MapPin } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getRegionFromZipCode } from '@/data/regional-climate'
import { ZIP_INPUT_ATTRS, sanitizeZip } from '@/lib/zip-input'

interface ZipFieldProps {
  value: string
  onChange: (zip: string) => void
  className?: string
}

// Compact ZIP input. Live-updates as the user types; shows a region chip once
// we resolve the 5-digit value to a known region. No submit button — the
// scoring engine reacts to the value directly.
export function ZipField({ value, onChange, className }: ZipFieldProps) {
  const isFiveDigits = /^\d{5}$/.test(value)
  const region = isFiveDigits ? getRegionFromZipCode(value) : 'Unknown'
  const resolved = isFiveDigits && region !== 'Unknown'

  return (
    <label
      className={cn(
        'flex w-full items-center gap-2 rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:w-auto',
        className,
      )}
    >
      <span className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700">
        <MapPin className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <span className="block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Your ZIP (optional)
        </span>
        <input
          {...ZIP_INPUT_ATTRS}
          placeholder="30309"
          value={value}
          onChange={(e) => onChange(sanitizeZip(e.target.value))}
          className="w-24 border-none bg-transparent p-0 text-sm font-semibold text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          aria-label="ZIP code for climate-zone scoring"
        />
      </div>
      {resolved && (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-1 text-xs font-semibold text-green-700">
          {region}
        </span>
      )}
    </label>
  )
}
